import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FormStep1, FormStep2 } from './FromStepper';
import AuthContainer from '@/components/Container/AuthContainer';
import { useAppSelector } from '@/redux/store.hook';
import { getAuthSelector } from '@/redux/auth/auth.slice';
import { getExternalProviders, startExternalAuth } from '@/api/externalAuth.api';
import type { ExternalProviderDescriptor } from '@/types/externalAuth';
import { errorHandler } from '@/utils/toastHandler';

const SignUpPage = () => {
  // @ts-ignore
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isLogin, userData } = useAppSelector(getAuthSelector);
  const [searchParams, setSearchParams] = useSearchParams();

  const [activeStep, setActiveStep] = useState<number>(1);
  const [userMail, setUserMail] = useState<string>('');
  const [showAccount, setShowAccount] = useState<string>('');
  const [externalProviders, setExternalProviders] = useState<ExternalProviderDescriptor[]>([]);

  useEffect(() => {
    if (isLogin && userData.acc_coname) {
      navigate('/home');
    }

    if (searchParams.get('lang')) {
      // 驗證語系是否在支援的語系內, 若不在則預設為i18n的語系
      const lang = ['sc', 'tc', 'en'].includes(searchParams.get('lang') as string)
        ? searchParams.get('lang')
        : i18n.language;
      i18n.changeLanguage(lang!);
    }

    // 將coupon存入localStorage中
    if(searchParams.get('coupon')) {
      localStorage.setItem('coupon', searchParams.get('coupon')!);
    }

    const fragment = new URLSearchParams(window.location.hash.replace(/^#/, ''));
    const externalInvitation = fragment.get('external_invitation');
    if (externalInvitation && /^[a-f0-9]{64}$/i.test(externalInvitation)) {
      window.sessionStorage.setItem('external.subaccountInvitationToken', externalInvitation);
      window.history.replaceState({}, document.title, window.location.pathname + window.location.search);
    }

    if (process.env.REACT_APP_EXTERNAL_REGISTRATION_ENABLED === 'true') {
      void getExternalProviders()
        .then((response) => setExternalProviders(response.data.data.providers.filter((item) => item.enabled_for_registration)))
        .catch(() => setExternalProviders([]));
    }
  },[]);

  const beginExternalRegistration = async (provider: string, turnstileToken: string | null) => {
    try {
      const response = await startExternalAuth(provider, 'register', null, turnstileToken);
      window.location.assign(response.data.data.authorization_url);
      return true;
    } catch (error: any) {
      errorHandler(error.response?.data?.message || t('pages.auth.external.failed'));
      return false;
    }
  };

  return (
    <AuthContainer title={activeStep === 2 ? t('multiFactorAuthentication') : t('registration')}>
      {activeStep === 1 && <FormStep1
        setShowAccount={setShowAccount}
        setActiveStep={setActiveStep}
        setUserMail={setUserMail}
        externalProviders={externalProviders}
        onExternalRegistration={beginExternalRegistration}
      />}
      {activeStep === 2 && <FormStep2 userMail={userMail} showAccount={showAccount} />}
    </AuthContainer>
  );
};

export default SignUpPage;
